import React, { useState } from 'react';
import {
  Flex,
  Box,
  FormControl,
  FormLabel,
  Input,
  Button,
  Heading,
  Text,
  VStack,
  useColorModeValue,
  InputGroup,
  InputRightElement,
  Link,
  Checkbox,
  useToast,
  Alert,
  AlertIcon
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router-dom';
import { getConfigValue } from '../../config/runtimeConfig';

const LoginPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const { loginWithRedirect, isLoading } = useAuth0();
  const location = useLocation();
  const toast = useToast();
  const bgColor = useColorModeValue('gray.50', 'gray.800');
  const cardBg = useColorModeValue('white', 'gray.700');
  const allowedDomain = getConfigValue(
    'REACT_APP_ALLOWED_EMAIL_DOMAIN',
    process.env.REACT_APP_ALLOWED_EMAIL_DOMAIN,
    'teliviit.com'
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    if (!email.toLowerCase().endsWith(`@${allowedDomain.toLowerCase()}`)) {
      setError(`Only @${allowedDomain} emails are allowed`);
      return;
    }

    try {
      await loginWithRedirect({
        authorizationParams: {
          connection: 'Username-Password-Authentication',
          login_hint: email,
          redirect_uri: window.location.origin + '/callback'
        },
        appState: {
          returnTo: location.state?.from?.pathname || '/dashboard',
          rememberMe
        }
      });
    } catch (err) {
      toast({
        title: 'Login failed',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Flex minH="100vh" align="center" justify="center" bg={bgColor}>
      <Box
        as="form"
        onSubmit={handleSubmit}
        w="full"
        maxW="md"
        p={8}
        borderRadius="lg"
        boxShadow="xl"
        bg={cardBg}
      >
        <VStack spacing={6} align="stretch">
          <Box textAlign="center">
            <Heading as="h1" size="xl" mb={2}>
              Welcome Back
            </Heading>
            <Text color="gray.500">Sign in to your @{allowedDomain} account</Text>
          </Box>

          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {error}
            </Alert>
          )}

          <FormControl id="email" isRequired>
            <FormLabel>Email Address</FormLabel>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={`your@${allowedDomain}`}
              focusBorderColor="blue.500"
              size="lg"
            />
          </FormControl>

          <FormControl id="password" isRequired>
            <FormLabel>Password</FormLabel>
            <InputGroup>
              <Input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                focusBorderColor="blue.500"
                size="lg"
              />
              <InputRightElement h="full">
                <Button
                  variant="ghost"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <ViewOffIcon /> : <ViewIcon />}
                </Button>
              </InputRightElement>
            </InputGroup>
          </FormControl>

          <Flex justify="space-between" align="center">
            <Checkbox
              isChecked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              colorScheme="blue"
            >
              Remember me
            </Checkbox>
            <Link color="blue.500" href="/forgot-password">
              Forgot password?
            </Link>
          </Flex>

          <Button
            type="submit"
            colorScheme="blue"
            size="lg"
            fontSize="md"
            width="full"
            isLoading={isLoading}
            loadingText="Signing in..."
          >
            Sign In
          </Button>

          <Text textAlign="center" mt={4}>
            Don't have an account?{' '}
            <Link color="blue.500" href="/signup">
              Sign up
            </Link>
          </Text>
        </VStack>
      </Box>
    </Flex>
  );
};

export default LoginPage;
